import { useState } from "react";

import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { FieldArray, useField } from "formik";
import { findIndexById, noop } from "neetocist";
import { Plus } from "neetoicons";
import { Button } from "neetoui";
import PropTypes from "prop-types";
import { prop } from "ramda";
import { useTranslation } from "react-i18next";

import BulkOptionModal from "./BulkOptionModal";
import { DEFAULT_OPTION_LABEL } from "./constants";
import FormError from "./FormError";
import useOptionFields from "./hooks/useOptionFields";
import "./option-fields.scss";
import OptionField from "./OptionField";
import {
  getOrderedOptions,
  getUniqueOptionId,
  resolveFormikValue,
  getMultiInputError,
} from "./utils";

const OptionFields = ({
  name,
  error,
  onDelete,
  onClone,
  onChange,
  onBlur,
  onEdit = noop,
  buttonProps = {},
  minOptions = 0,
  draggable = false,
  editable = true,
  disabled = false,
  isAddOptionEnabled = true,
  isDeleteOptionEnabled = true,
  isCloneOptionEnabled = false,
  isCheckboxEnabled = false,
  isPictureChoice = false,
  isTextArea = false,
  isMultiInput = false,
  isEditor = false,
  inputConfig = [],
  shouldDestroy = false,
  destroyFlagName = "deleted",
  shouldResetEmptyOptionOnBlur = true,
  isNewItemsPrefilled = true,
  itemLabel = DEFAULT_OPTION_LABEL,
  bulkActionProps = { enabled: false, bulkAddHelpDocUrl: "" },
}) => {
  const { t } = useTranslation();
  const [isBulkOptionModalOpen, setIsBulkOptionModalOpen] = useState(false);
  const [field, meta] = useField(name);
  const fieldError = meta.touched ? meta.error : null;

  const multiInputError = getMultiInputError(isMultiInput, meta);

  const options = field.value ?? [];

  const {
    shouldFocus,
    handleAddOption,
    handleBulkAddOption,
    handleChange,
    handleKeyDown,
    handleDelete,
    handleDragEnd,
    handleClone,
  } = useOptionFields({
    options,
    onChange,
    shouldDestroy,
    itemLabel,
    shouldResetEmptyOptionOnBlur,
    destroyFlagName,
    name,
    isNewItemsPrefilled,
    isAddOptionEnabled,
    isPictureChoice,
    isMultiInput,
    inputConfig,
  });

  // Temporarily disables the modifications to options when changed are getting saved to server
  // This condition only needed for server id generated types
  let isEditable = true;
  if (shouldDestroy) isEditable = options.every(prop("id") || error);

  const orderedOptions = getOrderedOptions(options);
  const filteredOptions = orderedOptions.filter(
    ({ [destroyFlagName]: deleted }) => !deleted
  );

  const hideDeleteOption =
    filteredOptions.length <= minOptions || disabled || !isDeleteOptionEnabled;
  const hideCloneOption = disabled || !isCloneOptionEnabled;

  const optionFieldProps = {
    disabled,
    draggable,
    editable,
    handleChange,
    handleDeleteClick: onDelete ?? handleDelete,
    handleCloneClick: onClone ?? handleClone,
    handleKeyDown,
    hideDeleteOption,
    hideCloneOption,
    inputConfig,
    isEditable,
    isMultiInput,
    isPictureChoice,
    isTextArea,
    itemLabel,
    onEdit,
    onBlur,
    shouldFocus,
    isCheckboxEnabled,
    isEditor,
  };

  // To fix the element positioned incorrectly while dragging when in a pane or modal.
  // https://github.com/hello-pangea/dnd/issues/560#issuecomment-1679201455
  const renderDragClone = (provided, _, rubric) => {
    const draggingIndex = rubric["source"].index;
    const option = filteredOptions[draggingIndex];
    const formikIndex = findIndexById(option.id, options);

    return (
      <OptionField
        {...{ ...optionFieldProps, option }}
        dragHandleProps={provided.dragHandleProps}
        innerRef={null}
        name={`${name}.${formikIndex}.label`}
        optionIndex={draggingIndex}
        draggableProps={{
          ...provided.draggableProps,
          style: { ...provided.draggableProps.style, zIndex: 999999 },
        }}
      />
    );
  };

  return (
    <form onSubmit={e => e.preventDefault()}>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable
          droppableId={`neeto-molecules-option-fields-${name}`}
          isDropDisabled={!draggable || disabled}
          renderClone={renderDragClone}
        >
          {({ innerRef, droppableProps, placeholder }) => (
            <div ref={innerRef} {...droppableProps}>
              <FieldArray {...{ name }}>
                {arrayHelpers => {
                  const options = resolveFormikValue(
                    name,
                    arrayHelpers.form.values
                  );

                  return options?.map((option, index) => {
                    if (option?.[destroyFlagName]) return null;

                    const optionIndex = findIndexById(
                      option.id,
                      filteredOptions
                    );

                    return (
                      <Draggable
                        dataKey={getUniqueOptionId(option)}
                        draggableId={getUniqueOptionId(option)}
                        index={optionIndex}
                        isDragDisabled={!draggable || disabled}
                        key={getUniqueOptionId(option)}
                      >
                        {({ innerRef, draggableProps, dragHandleProps }) => (
                          <OptionField
                            {...{
                              ...optionFieldProps,
                              arrayHelpers,
                              dragHandleProps,
                              draggableProps,
                              innerRef,
                              option,
                              optionIndex,
                            }}
                            name={`${name}.${index}.label`}
                          />
                        )}
                      </Draggable>
                    );
                  });
                }}
              </FieldArray>
              {placeholder}
              <FormError error={error ?? multiInputError ?? fieldError} />
            </div>
          )}
        </Droppable>
        <div className="mt-4 flex items-center justify-between">
          {isAddOptionEnabled && (
            <Button
              data-testid="add-option-link"
              disabled={!isEditable}
              icon={Plus}
              iconPosition="left"
              label={t("neetoMolecules.optionFields.addOption")}
              size="small"
              style="link"
              onClick={handleAddOption}
              {...buttonProps}
            />
          )}
          {bulkActionProps.enabled && !isPictureChoice && !isMultiInput && (
            <Button
              data-testid="add-bulk-option-link"
              disabled={!isEditable}
              label={t("neetoMolecules.optionFields.addBulk")}
              size="small"
              style="text"
              onClick={() => setIsBulkOptionModalOpen(true)}
            />
          )}
        </div>
      </DragDropContext>
      <BulkOptionModal
        bulkAddHelpDocUrl={bulkActionProps.bulkAddHelpDocUrl}
        handleBulkAdd={handleBulkAddOption}
        isOpen={isBulkOptionModalOpen}
        onClose={() => setIsBulkOptionModalOpen(false)}
      />
    </form>
  );
};

OptionFields.propTypes = {
  /**
   * Props to be passed to the add option button
   */
  buttonProps: PropTypes.object,

  /**
   * Enable drag and drop functionality
   */
  draggable: PropTypes.bool,

  /**
   * Enable edit functionality
   */
  editable: PropTypes.bool,

  /**
   * Error message to be displayed
   */
  error: PropTypes.string,

  /**
   * The field name in Formik state.
   */
  name: PropTypes.string,

  /**
   * Function to handle option deletion
   */
  onDelete: PropTypes.func,

  /**
   * Function to handle option cloning
   */
  onClone: PropTypes.func,

  /**
   * Function to handle option edit
   *
   */
  onEdit: PropTypes.func,

  /**
   * Minimum number of options required
   */
  minOptions: PropTypes.number,

  /**
   * Options array
   */
  options: PropTypes.array,

  /**
   * Prop to switch between Input and Textarea
   */
  isTextArea: PropTypes.bool,

  /**
   * Function to handle options change
   */
  onChange: PropTypes.func,

  /**
   * Prop to switch between PictureChoice and normal options
   * */
  isPictureChoice: PropTypes.bool,
  /**
   * When enabled, the options will have a rich text editor instead of a text input field.
   */
  isEditor: PropTypes.bool,
  /**
   * Enables selection checkbox for the options.
   */
  isCheckboxEnabled: PropTypes.bool,
  /**
   * Prop to set the item label
   * */

  itemLabel: PropTypes.string,

  /**
   * Props for bulk action
   */
  bulkActionProps: PropTypes.shape({
    enabled: PropTypes.bool,
    bulkAddHelpDocUrl: PropTypes.string,
  }),
  /**
   * To disable the input fields.
   */
  disabled: PropTypes.bool,
  /**
   * This prop can be used to show/hide the `Add option` button below the options list.
   */
  isAddOptionEnabled: PropTypes.bool,
  /**
   * This prop can be used to show/hide the delete button in each of the option.
   */
  isDeleteOptionEnabled: PropTypes.bool,
  /**
   * This prop can be used to show/hide the clone button in each of the option.
   */
  isCloneOptionEnabled: PropTypes.bool,
  /**
   * Enable multi-input mode for each option
   */
  isMultiInput: PropTypes.bool,
  /**
   * Configuration array for multi-input fields. Each object should have 'type' and optional 'placeholder', 'min', 'max'
   */
  inputConfig: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.oneOf(["text", "number"]),
      placeholder: PropTypes.string,
      min: PropTypes.number,
      max: PropTypes.number,
      defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ),
  /**
   * The input fields emptied by the user will be reset to the original value
   * when the field is blurred. This prop can be used to disable resetting the empty value on blur.
   */
  shouldResetEmptyOptionOnBlur: PropTypes.bool,
  /**
   * Whether the option should be soft deleted. If yes, the specified destroy flag name
   * will be set to true, otherwise it will be removed from the array.
   */
  shouldDestroy: PropTypes.bool,
  /**
   * The flag to set to true when the shouldDestroy is set to true.
   * Default value is `deleted`.
   */
  destroyFlagName: PropTypes.string,
  /**
   * Function to be called when input field is blurred. The arguments includes
   * the field id and the value.
   */
  onBlur: PropTypes.func,
  /**
   * Whether to set a default value to the label of the new items added.
   * Default value is true, the items will have the `itemLabel` + index as the default value.
   * If set to false, the initial value will be "".
   */
  isNewItemsPrefilled: PropTypes.bool,
};

export default OptionFields;
